import { useState, useEffect } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
import {
    Dialog,
    DialogContent,
    Button,
    InputBase,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Box,
    Typography,
    Divider,
    IconButton,
    Avatar,
    Tooltip,
    Tabs,
    Tab
} from '@mui/material';
import { 
    Close, 
    FormatBold, 
    FormatItalic, 
    FormatUnderlined, 
    AttachFile, 
    Link as LinkIcon,
    Image,
    DeleteOutline
} from '@mui/icons-material';
import { useGetEmailAccounts, useGetGmailOAuthUrl } from '../../react-query/email-account-queries';
import { useSendPitchEmail, useUpdatePitchDraft, useGetPitchEmails } from '../../react-query/outreach-queries';
import { useRevealPodcastEmail } from '../../react-query/podcast-queries';
import { EmailAccount } from '../../types/user';
import { useUser } from '../../providers/user-provider';
import { EmailMessage } from '../../types/outreach';

interface ComposePitchModalProps {
    open: boolean;
    onClose: () => void;
    pitchId: string;
    podcastId?: string;
    recipientEmail?: string | null;
    initialSubject?: string;
    initialBody?: string;
    onSuccess?: () => void;
}

export default function ComposePitchModal({
    open,
    onClose,
    pitchId,
    podcastId,
    recipientEmail,
    initialSubject = '',
    initialBody = '',
    onSuccess
}: ComposePitchModalProps) {
    const [accountId, setAccountId] = useState<string>('');
    const [to, setTo] = useState(recipientEmail || '');
    const [subject, setSubject] = useState(initialSubject);
    const [body, setBody] = useState(initialBody || '');
    const [error, setError] = useState<string | null>(null);
    const [tabValue, setTabValue] = useState(0); // 0 = Reply, 1 = New Thread

    const { data: accountsResponse, isLoading: accountsLoading } = useGetEmailAccounts();
    const { data: emails } = useGetPitchEmails(pitchId);
    const revealEmailMutation = useRevealPodcastEmail();
    
    const sendEmailMutation = useSendPitchEmail();
    const saveDraftMutation = useUpdatePitchDraft();
    const { refetch: fetchOAuthUrl, isFetching: isUrlLoading } = useGetGmailOAuthUrl();
    const { user } = useUser();
    
    const hasHistory = emails && emails.length > 0;

    useEffect(() => {
        if (!recipientEmail && podcastId && open) {
            revealEmailMutation.mutate(podcastId, {
                onSuccess: (response: any) => {
                    // response.data can act differently based on the shape, let's be safe
                    const email = response?.data?.email || response?.email || response; 
                    if (typeof email === 'string') {
                         setTo(email);
                    }
                }
            });
        } else if (recipientEmail) {
            setTo(recipientEmail);
        }
    }, [recipientEmail, podcastId, open]);

    useEffect(() => {
        if (!hasHistory) {
            setTabValue(1);
        } else {
            setTabValue(0);
        }
    }, [hasHistory]);

    const handleLinkAccount = async () => {
        try {
            console.log("Initiating OAuth flow...");
            const result = await fetchOAuthUrl();
            const data = result.data;
            if (data?.success && data?.data) {
                 const oauthData = data.data as any; 
                 const url = oauthData.authorizationUrl || oauthData; 
                 if (typeof url === 'string') {
                    window.location.href = url;
                 }
            }
        } catch (error) {
            setError("Failed to initiate Google login.");
        }
    };

    const accounts = accountsResponse?.data || [];
    
    useEffect(() => {
        if (open) {
            if (recipientEmail) {
                setTo(recipientEmail);
            } else if (podcastId && !to) {
                // If no email provided but we have podcast ID, try to fetch/reveal it
                revealEmailMutation.mutate(podcastId);
            }
        }
    }, [open, recipientEmail, podcastId]);

    // Handle reveal success
    useEffect(() => {
        if (revealEmailMutation.data?.success && revealEmailMutation.data?.data?.email) {
            setTo(revealEmailMutation.data.data.email);
        }
    }, [revealEmailMutation.data]);

    useEffect(() => {
        if (initialSubject) setSubject(initialSubject);
    }, [initialSubject]);

    useEffect(() => {
        if (initialBody) setBody(initialBody);
    }, [initialBody]);

    useEffect(() => {
        if (accounts.length > 0 && accountId === '') {
            const defaultAccount = accounts.find((acc: EmailAccount) => acc.isDefault);
            if (defaultAccount) {
                setAccountId(defaultAccount.id);
            } else {
                setAccountId(accounts[0].id);
            }
        }
    }, [accounts, accountId]);

    const handleSaveDraft = () => {
        saveDraftMutation.mutate({
            pitchId,
            data: { draftSubject: subject, draftBody: body }
        });
    };

    const handleSubmit = () => {
        let createNewThread = false;
        let inReplyToMessageId = null;

        if (hasHistory && tabValue === 0) {
            createNewThread = false;
            // Get last message ID for In-Reply-To
            if (emails && emails.length > 0) {
                // Sort by sentAt desc just to be safe if not already sorted
                const sorted = [...emails].sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
                const lastEmail = sorted[0];
                if (lastEmail.googleMessageId) {
                    inReplyToMessageId = lastEmail.googleMessageId;
                }
            }
        } else {
            createNewThread = true;
        }

        if (accountId === '') {
            setError('Please select a sender account.');
            return;
        }
        if (!to) {
            setError('Recipient is required.');
            return;
        }
        if (!subject) {
            setError('Subject is required.');
            return;
        }
        if (!body) {
            setError('Body is required.');
            return;
        }

        // Send Pitch Email
        sendEmailMutation.mutate({
            pitchId,
            data: {
                accountId,
                subject,
                body,
                createNewThread,
                inReplyToMessageId
            }
        }, {
            onSuccess: () => {
                onSuccess?.();
                onClose();
                setBody(''); 
            },
            onError: (err: any) => {
                const msg = err.response?.data?.message || 'Failed to send email. Please try again.';
                setError(msg);
            }
        });
    };

    const handleClose = () => {
        onClose();
    };

    if (user?.subscriptionTier !== 'ENTERPRISE') {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Upgrade to Enterprise
                    </Typography>
                    <Typography color="text.secondary" paragraph>
                        Outreach and email features are available exclusively on the Enterprise plan. 
                        Connect your Gmail and send personalized pitches directly within the platform.
                    </Typography>
                    <Button variant="contained" onClick={handleClose} sx={{ mt: 2 }}>
                        Got it
                    </Button>
                </Box>
            </Dialog>
        );
    }
            
    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            fullWidth 
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    height: '80vh',
                    maxHeight: 800,
                    boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }
            }}
        >
            {/* Header */}
            <Box sx={{ 
                px: 2, 
                py: 1.5, 
                bgcolor: '#f8fafc', 
                borderBottom: '1px solid', 
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#1e293b' }}>
                    New Message
                </Typography>
                <Box>
                     <Tooltip title="Minimize">
                        <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
                     </Tooltip>
                </Box>
            </Box>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto' }}>
                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ m: 2, borderRadius: 2 }}>{error}</Alert>
                )}
                
                {accountsLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" flex={1}><CircularProgress /></Box>
                ) : accounts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8, px: 4 }}>
                        <Typography variant="h6" gutterBottom>No email account connected</Typography>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            Connect your Gmail account to start sending pitches directly from here.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={handleLinkAccount}
                            disabled={isUrlLoading}
                            sx={{ borderRadius: 2, textTransform: 'none' }}
                        >
                            {isUrlLoading ? 'Redirecting...' : 'Link Gmail Account'}
                        </Button>
                    </Box>
                ) : (
                    <>
                         {/* Reply / New Thread Toggle */}
                         {hasHistory && (
                             <Box sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Tabs value={tabValue} onChange={(_, val) => setTabValue(val)} sx={{
                                    minHeight: 48,
                                    '& .MuiTab-root': { textTransform: 'none', minHeight: 48, fontWeight: 600 }
                                }}>
                                    <Tab label="Reply to Thread" />
                                    <Tab label="New Conversation" />
                                </Tabs>
                             </Box>
                        )}

                        {/* Address Fields */}
                        <Box sx={{ px: 3, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                            {/* To Field (Read-only Tile) */}
                            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 48, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary', width: 60, flexShrink: 0 }}>To</Typography>
                                {to ? (
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: '#e0f2fe', color: '#0369a1', borderRadius: 4, px: 1.5, py: 0.5 }}>
                                        <Typography variant="body2" fontWeight={500}>{to}</Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary" fontStyle="italic">No recipient</Typography>
                                )}
                            </Box>
                            
                            {/* From Field */}
                            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 48, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary', width: 60, flexShrink: 0 }}>From</Typography>
                                <Select
                                    value={accountId}
                                    onChange={(e) => setAccountId(e.target.value)}
                                    variant="standard"
                                    disableUnderline
                                    sx={{ 
                                        fontSize: '0.95rem', 
                                        flex: 1,
                                        '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center', gap: 1 } 
                                    }}
                                >
                                    {accounts.map((acc: EmailAccount) => (
                                        <MenuItem key={acc.id} value={acc.id}>
                                            <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'primary.main', mr: 1 }}>
                                                {(acc.emailAddress?.[0] || '?').toUpperCase()}
                                            </Avatar>
                                            {acc.emailAddress}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            {/* Subject Field */}
                            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 48 }}>
                                <InputBase 
                                    fullWidth 
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Subject"
                                    sx={{ fontSize: '0.95rem', fontWeight: 500 }}
                                />
                            </Box>
                        </Box>

                        {/* Editor Area (Body) */}
                        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', minHeight: 200, flexShrink: 0 }}>
                            <InputBase
                                fullWidth
                                multiline
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                placeholder="Write your pitch here..."
                                sx={{ 
                                    flex: 1, 
                                    alignItems: 'flex-start',
                                    fontSize: '1rem',
                                    '& textarea': { height: '100% !important' }
                                }}
                            />
                        </Box>

                        {/* History (Previous Messages) */}
                         {emails && emails.length > 0 && (
                            <Box sx={{ bgcolor: '#f8fafc', borderTop: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ px: 3, py: 2, bgcolor: '#f1f5f9', borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Previous Messages
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    {[...emails].reverse().map((msg: EmailMessage) => {
                                        const isOutbound = msg.direction === 'OUTBOUND';
                                        
                                        // Sender Name Logic
                                        const senderName = isOutbound ? 'You' : (msg.sender || 'Unknown');
                                        const initial = senderName.charAt(0).toUpperCase();

                                        return (
                                            <Box 
                                                key={msg.id} 
                                                sx={{ 
                                                    display: 'flex', 
                                                    gap: 2,
                                                    alignItems: 'flex-start',
                                                    width: '100%'
                                                }}
                                            >
                                                <Avatar 
                                                    sx={{ 
                                                        width: 36, 
                                                        height: 36, 
                                                        bgcolor: isOutbound ? 'primary.main' : 'grey.400',
                                                        fontSize: '0.9rem'
                                                    }}
                                                >
                                                    {initial}
                                                </Avatar>

                                                <Box sx={{ 
                                                    flex: 1, 
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 2,
                                                    overflow: 'hidden',
                                                    bgcolor: 'white'
                                                }}>
                                                    {/* Message Header */}
                                                    <Box sx={{ 
                                                        px: 2, 
                                                        py: 1.5, 
                                                        bgcolor: isOutbound ? '#f0f9ff' : '#f8fafc',
                                                        borderBottom: '1px solid',
                                                        borderColor: 'divider',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="subtitle2" fontWeight={600}>
                                                                {senderName}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {isOutbound ? 'sent to' : 'from'} {isOutbound ? to : 'You'}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(msg.sentAt).toLocaleDateString(undefined, { 
                                                                weekday: 'short',
                                                                month: 'short', 
                                                                day: 'numeric', 
                                                                hour: 'numeric', 
                                                                minute: '2-digit' 
                                                            })}
                                                        </Typography>
                                                    </Box>

                                                    {/* Message Body */}
                                                    <Box sx={{ p: 2 }}>
                                                        <div 
                                                            dangerouslySetInnerHTML={{ __html: msg.bodyHtml }} 
                                                            style={{ 
                                                                fontSize: '0.95rem', 
                                                                color: '#334155',
                                                                lineHeight: 1.6,
                                                                wordBreak: 'break-word',
                                                                fontFamily: 'inherit'
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            {/* Footer with Toolbar */}
            <Box sx={{ 
                p: 2, 
                px: 3,
                borderTop: '1px solid', 
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'white',
                gap: 2
            }}>
                <Button 
                    variant="outlined"
                    onClick={handleSaveDraft}
                    disabled={saveDraftMutation.isPending}
                    sx={{ 
                        borderRadius: '20px', 
                        px: 3, 
                        py: 1,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        fontWeight: 600
                    }}
                >
                    {saveDraftMutation.isPending ? 'Saving...' : 'Save Draft'}
                </Button>

                <Button 
                    variant="contained" 
                    onClick={handleSubmit} 
                    disabled={sendEmailMutation.isPending || accounts.length === 0}
                    sx={{ 
                        borderRadius: '20px', 
                        px: 4, 
                        py: 1,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        boxShadow: 'none',
                        '&:hover': { boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }
                    }}
                >
                    {sendEmailMutation.isPending ? 'Sending...' : 'Send'}
                </Button>

                {/* Fake Formatting Toolbar for "Professional" look */}
                <Box sx={{ display: 'flex', color: 'text.secondary', gap: 0.5 }}>
                    <IconButton size="small"><FormatBold fontSize="small" /></IconButton>
                    <IconButton size="small"><FormatItalic fontSize="small" /></IconButton>
                    <IconButton size="small"><FormatUnderlined fontSize="small" /></IconButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 20, alignSelf: 'center' }} />
                    <IconButton size="small"><AttachFile fontSize="small" /></IconButton>
                    <IconButton size="small"><LinkIcon fontSize="small" /></IconButton>
                    <IconButton size="small"><Image fontSize="small" /></IconButton>
                </Box>

                <Box sx={{ flex: 1 }} />

                <IconButton size="small" onClick={() => setBody('')} color="default">
                    <DeleteOutline />
                </IconButton>
            </Box>
        </Dialog>
    );
}
